import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SendHorizontalIcon,
  MessageCircleIcon,
  HashIcon,
  PlusIcon,
  SearchIcon,
  StickyNoteIcon,
  XIcon,
  PencilIcon,
  Trash2Icon,
  EyeOffIcon,
  PaperclipIcon,
  FileTextIcon,
} from 'lucide-react';
import {
  BON_EXT,
  BON_MAX_BYTES,
  DM_BIJLAGEN_MAX,
  type DmBijlage,
  type DmConversation,
  type MessageSearchHit,
  type SmoelenboekEntry,
  type ThemaThread,
} from '@kennisbank/shared';
import { roleIsAdmin } from '@kennisbank/shared';
import { Layout, OnlineDot, useOnlineUserIds } from '../components/Layout.js';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { dayLabel, initials, listTime } from '../lib/format.js';

// DM and channel messages share the fields the thread pane renders.
type Msg = {
  id: string;
  senderId: string;
  text: string;
  /** Alleen DM's; kanalen hebben (nog) geen bijlagen. */
  bijlagen?: DmBijlage[];
  createdAt: string;
  /** Alleen client-side: optimistisch geplaatst bericht dat nog onderweg is of mislukte. */
  status?: 'sending' | 'failed';
};

// The currently open conversation: either a 1-on-1 DM or a thema-kanaal.
type Active = { kind: 'dm' | 'channel'; id: string } | null;

function otherParticipantId(conv: DmConversation, myId: string): string {
  return conv.participants.find((p) => p !== myId) ?? conv.participants[0] ?? '';
}

/** Gesprek met jezelf: beide deelnemers zijn dezelfde gebruiker (o.a. "info vragen" op je eigen verzoek). */
function isSelfConv(conv: DmConversation): boolean {
  return conv.participants[0] === conv.participants[1];
}

const ZELFGESPREK = 'Notities aan jezelf';

/** randomUUID bestaat alleen in een secure context (https/localhost); op http://<lan-ip> niet. */
function nieuwId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Previews die alleen uit een technische code bestaan (bijv. een TimeChimp-id
 * dat via een melding binnenkwam) zeggen niets in een lijst; toon dan "Bericht".
 */
function bestandsgrootte(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} kB`
    : `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Afbeeldingen inline, de rest als bestandsregel; openen in een nieuw tabblad. */
function Bijlagen({ bijlagen, isMine }: { bijlagen: DmBijlage[]; isMine: boolean }): JSX.Element {
  return (
    <div className="mb-1 flex flex-col gap-1.5">
      {bijlagen.map((b) =>
        b.mimeType.startsWith('image/') && b.url ? (
          <a key={b.object} href={b.url} target="_blank" rel="noreferrer" title={b.filename}>
            <img
              src={b.url}
              alt={b.filename}
              className="max-h-60 max-w-full rounded-lg object-contain"
            />
          </a>
        ) : (
          <a
            key={b.object}
            href={b.url}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12px] ${
              isMine ? 'bg-white/15 hover:bg-white/25' : 'bg-brand-50 hover:bg-brand-100'
            }`}
          >
            <FileTextIcon size={16} className="shrink-0" />
            <span className="min-w-0 truncate font-medium">{b.filename}</span>
            <span className={`shrink-0 ${isMine ? 'text-white/60' : 'text-ink-900/40'}`}>
              {bestandsgrootte(b.sizeBytes)}
            </span>
          </a>
        ),
      )}
    </div>
  );
}

function previewTekst(preview: string | undefined): { tekst: string; leeg: boolean } {
  if (!preview?.trim()) return { tekst: 'Nog geen berichten', leeg: true };
  // Minstens één cijfer, anders is een lang gewoon woord ook "technisch".
  if (/^(?=.*\d)[A-Z0-9_-]{16,}$/i.test(preview.trim())) return { tekst: 'Bericht', leeg: false };
  return { tekst: preview, leeg: false };
}

function DmItem({
  conv,
  otherUser,
  myId,
  isActive,
  onClick,
}: {
  conv: DmConversation;
  otherUser: SmoelenboekEntry | undefined;
  myId: string;
  isActive: boolean;
  onClick: () => void;
}): JSX.Element {
  const unread = conv.unreadCounts[myId] ?? 0;
  const otherId = otherParticipantId(conv, myId);
  const self = isSelfConv(conv);
  const name = self ? ZELFGESPREK : (otherUser?.displayName ?? otherId);
  const onlineIds = useOnlineUserIds();
  const online = !self && onlineIds.has(otherId);
  const preview = previewTekst(conv.lastMessagePreview);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
        isActive ? 'bg-brand-50' : 'hover:bg-brand-50/60'
      }`}
    >
      <div className="relative shrink-0">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100">
          {self ? (
            <StickyNoteIcon size={16} className="text-brand-700" />
          ) : (
            <span className="text-[13px] font-bold text-brand-700">{initials(name)}</span>
          )}
        </div>
        {online && <OnlineDot />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className={`truncate text-[13px] ${unread > 0 ? 'font-bold' : 'font-semibold'} text-ink-900`}
          >
            {name}
          </span>
          {conv.lastMessagePreview && (
            <span className="ml-auto shrink-0 text-[11px] text-ink-900/40">
              {listTime(conv.lastMessageAt ?? conv.createdAt)}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <p
            className={`truncate text-[12px] ${preview.leeg ? 'italic text-ink-900/35' : 'text-ink-900/50'}`}
          >
            {preview.tekst}
          </p>
          {unread > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ChannelItem({
  thread,
  unread,
  isActive,
  onClick,
}: {
  thread: ThemaThread;
  unread: number;
  isActive: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors ${
        isActive ? 'bg-brand-50' : 'hover:bg-brand-50/60'
      }`}
    >
      <HashIcon size={15} className="shrink-0 text-brand-600" />
      <span
        className={`truncate text-[13px] ${unread !== 0 ? 'font-bold text-ink-900' : 'font-medium text-ink-900/80'}`}
      >
        {thread.name}
      </span>
      {thread.lastMessageAt && (
        <span className="ml-auto shrink-0 text-[11px] text-ink-900/40">
          {listTime(thread.lastMessageAt)}
        </span>
      )}
      {unread > 0 && (
        <span className="shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
      {unread < 0 && <span className="h-2 w-2 shrink-0 rounded-full bg-brand-600" />}
    </button>
  );
}

function MessageBubble({
  message,
  isMine,
  senderName,
  gelezen,
  onRetry,
  vervolg,
  vervolgd,
  onDelete,
}: {
  message: Msg;
  isMine: boolean;
  senderName?: string;
  /** "Gelezen" onder dit bericht: alleen het laatste eigen bericht krijgt dit. */
  gelezen?: boolean;
  onRetry?: (m: Msg) => void;
  /** Zelfde afzender als het vorige bericht, binnen vijf minuten: naam weg, minder ruimte. */
  vervolg?: boolean;
  /** Zelfde afzender als het volgende bericht, binnen vijf minuten: tijd pas onder de laatste. */
  vervolgd?: boolean;
  /** Eigen bericht (of admin in een kanaal): prullenbak bij hover. */
  onDelete?: (m: Msg) => void;
}): JSX.Element {
  const time = new Date(message.createdAt).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div
      className={`group flex flex-col ${isMine ? 'items-end' : 'items-start'} ${vervolg ? '-mt-2' : ''}`}
    >
      <div className={`flex max-w-[70%] items-center gap-1 ${isMine ? 'flex-row-reverse' : ''}`}>
        {onDelete && !message.status && (
          <button
            type="button"
            onClick={() => onDelete(message)}
            title="Bericht verwijderen"
            aria-label="Bericht verwijderen"
            className="shrink-0 rounded-md p-1 text-ink-900/30 opacity-40 transition-opacity hover:bg-red-50 hover:text-red-700 focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"
          >
            <Trash2Icon size={13} />
          </button>
        )}
        <div
          className={`min-w-0 rounded-2xl px-4 py-2.5 ${message.status === 'sending' ? 'opacity-60' : ''} ${
            isMine
              ? `bg-brand-600 text-white ${vervolgd ? '' : 'rounded-br-sm'}`
              : `bg-white text-ink-900 shadow-sm ring-1 ring-brand-100 ${vervolgd ? '' : 'rounded-bl-sm'}`
          }`}
        >
          {!isMine && senderName && !vervolg && (
            <p className="mb-0.5 text-[11px] font-semibold text-brand-700">{senderName}</p>
          )}
          {message.bijlagen?.length ? (
            <Bijlagen bijlagen={message.bijlagen} isMine={isMine} />
          ) : null}
          {message.text && (
            <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
              {message.text}
            </p>
          )}
          {!vervolgd && (
            <p
              className={`mt-1 text-right text-[10px] ${isMine ? 'text-white/60' : 'text-ink-900/40'}`}
            >
              {time}
            </p>
          )}
        </div>
      </div>
      {message.status === 'failed' && (
        <button
          type="button"
          onClick={() => onRetry?.(message)}
          className="mt-1 text-[11px] font-medium text-red-700 hover:underline"
        >
          Niet verzonden. Opnieuw proberen
        </button>
      )}
      {gelezen && <p className="mt-1 text-[10px] text-ink-900/40">Gelezen</p>}
    </div>
  );
}

/** Profielkaart bovenin een DM: vult de lege ruimte met wie je spreekt. */
function ProfielKaart({
  member,
  self,
  online,
}: {
  member: SmoelenboekEntry | undefined;
  self: boolean;
  online: boolean;
}): JSX.Element {
  if (self) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100">
          <StickyNoteIcon size={24} className="text-brand-700" />
        </div>
        <p className="text-[14px] font-semibold text-ink-900">{ZELFGESPREK}</p>
        <p className="max-w-xs text-[12px] text-ink-900/50">
          Alleen jij ziet dit gesprek. Handig voor losse aantekeningen of een bericht aan jezelf.
        </p>
      </div>
    );
  }
  if (!member) return <></>;
  const sub = [member.functietitel, member.regio].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <div className="relative">
        {member.photoUrl ? (
          <img
            src={member.photoUrl}
            alt=""
            className="h-14 w-14 rounded-full object-cover ring-2 ring-brand-100"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100">
            <span className="text-[18px] font-bold text-brand-700">
              {initials(member.displayName)}
            </span>
          </div>
        )}
        {online && <OnlineDot large />}
      </div>
      <p className="text-[14px] font-semibold text-ink-900">{member.displayName}</p>
      {sub && <p className="text-[12px] text-ink-900/50">{sub}</p>}
      {member.specialisaties && member.specialisaties.length > 0 && (
        <div className="flex max-w-sm flex-wrap justify-center gap-1">
          {member.specialisaties.map((sp, i) => (
            <span
              key={`${i}-${sp}`}
              className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
            >
              {sp}
            </span>
          ))}
        </div>
      )}
      <p className="text-[11px] text-ink-900/40">{online ? 'Nu online' : 'Niet online'}</p>
    </div>
  );
}

export function BerichtenPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const myId = user?.id ?? '';
  const isAdmin = user?.role === 'suppohrt_admin';
  // Kanalen beheert de beheerder van de eigen tenant (ook zorggroep_admin in
  // RHOGO); de backend schrijft altijd onder de eigen tenant.
  const isKanaalBeheerder = user ? roleIsAdmin(user.role) : false;
  // Demo-omgeving: alles buiten de demo-allowlist is server-side alleen-lezen
  // (middleware/demo.ts). Zonder deze poort loopt een verstuurd bericht op 403
  // en blijft er een rode 'mislukt'-bubbel staan midden in een demo.
  const demo = user?.demo === true;

  const [active, setActive] = useState<Active>(() => {
    const conv = searchParams.get('conv');
    const kanaal = searchParams.get('kanaal');
    if (conv) return { kind: 'dm', id: conv };
    if (kanaal) return { kind: 'channel', id: kanaal };
    return null;
  });
  const [conversations, setConversations] = useState<DmConversation[]>([]);
  const [threads, setThreads] = useState<ThemaThread[]>([]);
  const [reads, setReads] = useState<Record<string, string>>({});
  const [kanaalUnread, setKanaalUnread] = useState<Record<string, number>>({});
  // Scrollgedrag: alleen automatisch naar onder als de lezer daar al stond
  // (of zelf verstuurde); anders een knop "Nieuwe berichten". Reageert op een
  // nieuw laatste bericht-id, niet op elke poll (die geeft altijd een nieuwe array).
  const onderaanRef = useRef(true);
  const laatsteIdRef = useRef<string | undefined>(undefined);
  const [nieuwOnder, setNieuwOnder] = useState(false);
  const [members, setMembers] = useState<SmoelenboekEntry[]>([]);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Al geüploade bijlagen die met het volgende bericht meegaan.
  const [klaarBijlagen, setKlaarBijlagen] = useState<DmBijlage[]>([]);
  const [uploadBezig, setUploadBezig] = useState(false);
  const [bijlageFout, setBijlageFout] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState<string | null>(null); // null = search panel closed
  const [treffers, setTreffers] = useState<MessageSearchHit[] | 'fout' | null>(null); // null = niet gezocht
  const zoekSeq = useRef(0);
  // Bericht waar een zoektreffer naartoe wijst: even scrollen en oplichten.
  const [markeerId, setMarkeerId] = useState<string | null>(null);
  // Weggeklikte kanaalstrook per kanaal, per gebruiker bewaard op de server.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [newChannelName, setNewChannelName] = useState<string | null>(null); // null = form closed
  // Kanaal bewerken (admin): naam + onderschrift, inline in de kop.
  const [bewerkt, setBewerkt] = useState(false);
  const [bewerkNaam, setBewerkNaam] = useState('');
  const [bewerkOnderschrift, setBewerkOnderschrift] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Welk gesprek nu open is, voor callbacks die na een wissel nog binnenkomen.
  const activeRef = useRef<Active>(active);
  activeRef.current = active;
  // Volgnummer per GET: een oud antwoord dat na een nieuwer binnenkomt wordt
  // genegeerd, anders wist een trage poll een net verzonden bericht.
  const fetchSeq = useRef(0);
  // Mislukte berichten per gesprek, zodat ze een wissel overleven.
  const failedRef = useRef<Record<string, Msg[]>>({});

  const loadConversations = useCallback((): void => {
    void apiGet<{ conversations: DmConversation[] }>('/api/v1/dm/conversations')
      .then((d) => {
        setConversations(d.conversations);
        // Een open DM die niet (meer) in de lijst staat (verborgen, terug-knop,
        // oude link): sluiten in plaats van een spookkop met ruwe id's tonen.
        const a = activeRef.current;
        if (a?.kind === 'dm' && !d.conversations.some((c) => c.id === a.id)) {
          setActive(null);
          setSearchParams({});
        }
      })
      .catch(() => undefined);
  }, []);

  const loadThreads = useCallback((): void => {
    void apiGet<{
      threads: ThemaThread[];
      reads: Record<string, string>;
      unread: Record<string, number>;
    }>('/api/v1/thema-threads').then((d) => {
      setThreads(d.threads);
      setReads(d.reads);
      setKanaalUnread(d.unread ?? {});
    });
  }, []);

  // refreshLists alleen bij selectie/verzenden, niet bij elke poll-tick: op elke
  // tick de lijsten mee-herladen liep de globale rate limit (300/15 min) vol.
  const loadMessages = useCallback(
    (target: Active, refreshLists = false): void => {
      if (!target) return;
      const seq = ++fetchSeq.current;
      const base =
        target.kind === 'dm'
          ? `/api/v1/dm/conversations/${target.id}`
          : `/api/v1/thema-threads/${target.id}`;
      void apiGet<{ messages: Msg[] }>(`${base}/messages`)
        .then((d) => {
          if (seq !== fetchSeq.current || activeRef.current?.id !== target.id) return;
          const server = [...d.messages].reverse();
          // Lokale bubbels blijven staan zolang ze onderweg of mislukt zijn,
          // behalve als de server ze al terugstuurt (poll kruiste de POST).
          setMessages((ms) => [
            ...server,
            ...ms.filter(
              (m) =>
                m.status &&
                !server.some(
                  (s) =>
                    s.id === m.id || (s.senderId === m.senderId && s.text && s.text === m.text),
                ),
            ),
          ]);
          // Alleen als het tabblad zichtbaar is telt openstaan als lezen; en tot
          // het nieuwste bericht dat we daadwerkelijk tonen, niet "nu".
          if (document.hidden) return;
          const upTo = server[server.length - 1]?.createdAt;
          void apiPost(`${base}/read`, upTo ? { upTo } : {})
            .catch(() => undefined)
            .then(() => {
              if (refreshLists) (target.kind === 'dm' ? loadConversations : loadThreads)();
            });
        })
        .catch(() => undefined);
    },
    [loadConversations, loadThreads],
  );

  useEffect(() => {
    loadConversations();
    loadThreads();
    void apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek').then((d) =>
      setMembers(d.users),
    );
  }, [loadConversations, loadThreads]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active, true);
    // 15s/30s i.p.v. 5s: op 5s at één open Berichten-tab het hele
    // rate-limit-budget (300 req/15 min) in ~5 minuten op → 429 voor alles.
    const interval = setInterval(() => loadMessages(active), 15000);
    return () => clearInterval(interval);
  }, [active, loadMessages]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
      loadThreads();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadConversations, loadThreads]);

  useEffect(() => {
    const laatste = messages[messages.length - 1];
    if (!laatste || laatste.id === laatsteIdRef.current) return;
    const eersteLaad = laatsteIdRef.current === undefined;
    laatsteIdRef.current = laatste.id;
    if (eersteLaad || onderaanRef.current || laatste.status === 'sending') {
      messagesEndRef.current?.scrollIntoView({ behavior: eersteLaad ? 'auto' : 'smooth' });
      setNieuwOnder(false);
    } else {
      setNieuwOnder(true);
    }
  }, [messages]);

  // Zoeken in berichttekst, 300ms na de laatste toetsaanslag, vanaf 2 tekens.
  useEffect(() => {
    const q = search?.trim() ?? '';
    if (q.length < 2) {
      setTreffers(null);
      return;
    }
    const t = setTimeout(() => {
      const seq = ++zoekSeq.current;
      void apiGet<{ results: MessageSearchHit[] }>(`/api/v1/dm/search?q=${encodeURIComponent(q)}`)
        .then((d) => {
          if (seq === zoekSeq.current) setTreffers(d.results);
        })
        .catch(() => {
          if (seq === zoekSeq.current) setTreffers('fout');
        });
    }, 300);
    return () => {
      clearTimeout(t);
      zoekSeq.current++; // een antwoord op een oudere zoekterm telt niet meer
    };
  }, [search]);

  useEffect(() => {
    if (!markeerId || !messages.some((m) => m.id === markeerId)) return;
    document.getElementById(`msg-${markeerId}`)?.scrollIntoView({ block: 'center' });
    const t = setTimeout(() => setMarkeerId(null), 2500);
    return () => clearTimeout(t);
  }, [markeerId, messages]);

  // Invoerveld groeit mee tot zes regels; daarna scrolt het intern.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 6 * 22 + 20)}px`;
    // Het veld duwt de lijst omhoog; wie onderaan stond blijft onderaan.
    if (onderaanRef.current) messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [draft]);

  function select(target: Active): void {
    setMessages((ms) => {
      if (active) failedRef.current[active.id] = ms.filter((m) => m.status === 'failed');
      return target ? (failedRef.current[target.id] ?? []) : [];
    });
    setActive(target);
    onderaanRef.current = true;
    laatsteIdRef.current = undefined;
    setNieuwOnder(false);
    setSearch(null);
    setBewerkt(false); // anders bewerk je na het wisselen het verkeerde kanaal
    // Bijlagen horen bij het gesprek waarin ze geüpload zijn (de server weigert ze elders).
    setKlaarBijlagen([]);
    setBijlageFout(null);
    if (target?.kind === 'dm') setSearchParams({ conv: target.id });
    else if (target?.kind === 'channel') setSearchParams({ kanaal: target.id });
    else setSearchParams({});
  }

  // Optimistisch: de bubbel staat er meteen, de server bevestigt via
  // loadMessages. Mislukt het, dan blijft de bubbel staan met een retry-link
  // in plaats van dat de tekst terug het invoerveld in springt.
  async function verstuur(target: Active, msg: Msg): Promise<void> {
    if (!target) return;
    const path =
      target.kind === 'dm'
        ? `/api/v1/dm/conversations/${target.id}/messages`
        : `/api/v1/thema-threads/${target.id}/messages`;
    setMessages((ms) => [...ms.filter((m) => m.id !== msg.id), { ...msg, status: 'sending' }]);
    try {
      const d = await apiPost<{ message: Msg }>(path, {
        text: msg.text,
        id: msg.id,
        ...(msg.bijlagen?.length ? { bijlagen: msg.bijlagen } : {}),
      });
      // Serverversie (echte id + servertijd) in de plaats van de tijdelijke
      // bubbel; geen herlaad nodig, alleen de lijst met previews.
      if (activeRef.current?.id === target.id) {
        setMessages((ms) => ms.map((m) => (m.id === msg.id ? d.message : m)));
      }
      (target.kind === 'dm' ? loadConversations : loadThreads)();
    } catch {
      setMessages((ms) => ms.map((m) => (m.id === msg.id ? { ...m, status: 'failed' } : m)));
    }
  }

  async function handleSend(): Promise<void> {
    if (!active || (!draft.trim() && !klaarBijlagen.length) || sending || uploadBezig) return;
    setSending(true);
    const text = draft.trim();
    const bijlagen = klaarBijlagen;
    setDraft('');
    setKlaarBijlagen([]);
    try {
      await verstuur(active, {
        id: nieuwId(),
        senderId: myId,
        text,
        ...(bijlagen.length ? { bijlagen } : {}),
        createdAt: new Date().toISOString(),
      });
    } finally {
      setSending(false);
    }
  }

  // Browser-PUT direct naar GCS (zelfde patroon als declaratiebonnen); het
  // bericht draagt daarna alleen het objectpad.
  async function voegBijlagenToe(files: File[]): Promise<void> {
    if (!active || active.kind !== 'dm' || !files.length) return;
    setBijlageFout(null);
    if (klaarBijlagen.length + files.length > DM_BIJLAGEN_MAX) {
      setBijlageFout(`Maximaal ${DM_BIJLAGEN_MAX} bijlagen per bericht.`);
      return;
    }
    const convId = active.id;
    setUploadBezig(true);
    try {
      for (const file of files) {
        if (!(file.type in BON_EXT)) {
          throw new Error(`${file.name}: alleen PDF, afbeelding, Word of Excel.`);
        }
        if (file.size > BON_MAX_BYTES) {
          throw new Error(`${file.name} is groter dan ${BON_MAX_BYTES / (1024 * 1024)} MB.`);
        }
        const { uploadUrl, object } = await apiPost<{ uploadUrl: string; object: string }>(
          `/api/v1/dm/conversations/${convId}/bijlage-upload-url`,
          { filename: file.name, mimeType: file.type, sizeBytes: file.size },
        );
        const put = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'content-type': file.type },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload van ${file.name} is mislukt.`);
        if (activeRef.current?.id !== convId) return;
        setKlaarBijlagen((b) => [
          ...b,
          { object, filename: file.name, mimeType: file.type, sizeBytes: file.size },
        ]);
      }
    } catch (err) {
      setBijlageFout(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'De upload is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setUploadBezig(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function startDm(otherUserId: string): Promise<void> {
    const d = await apiPost<{ conversation: DmConversation }>('/api/v1/dm/conversations', {
      otherUserId,
    });
    loadConversations();
    select({ kind: 'dm', id: d.conversation.id });
  }

  async function saveChannel(thread: ThemaThread): Promise<void> {
    const naam = bewerkNaam.trim();
    if (!naam) return;
    const d = await apiPatch<{ thread: ThemaThread }>(`/api/v1/thema-threads/${thread.id}`, {
      name: naam,
      description: bewerkOnderschrift.trim(),
    });
    setThreads((ts) => ts.map((t) => (t.id === d.thread.id ? d.thread : t)));
    setBewerkt(false);
  }

  async function deleteMessage(target: Active, m: Msg): Promise<void> {
    if (!target || !window.confirm('Dit bericht definitief verwijderen?')) return;
    const path =
      target.kind === 'dm'
        ? `/api/v1/dm/conversations/${target.id}/messages/${m.id}`
        : `/api/v1/thema-threads/${target.id}/messages/${m.id}`;
    try {
      await apiDelete(path);
      setMessages((ms) => ms.filter((x) => x.id !== m.id));
      (target.kind === 'dm' ? loadConversations : loadThreads)();
    } catch {
      window.alert('Verwijderen is niet gelukt.');
    }
  }

  async function hideConversation(conv: DmConversation): Promise<void> {
    if (!window.confirm('Dit gesprek verbergen? Het komt terug zodra er een nieuw bericht is.'))
      return;
    try {
      await apiPost(`/api/v1/dm/conversations/${conv.id}/hide`, { upTo: conv.lastMessageAt });
      select(null);
      loadConversations();
    } catch {
      window.alert('Verbergen is niet gelukt.');
    }
  }

  // Verbergen is de gebruikersactie; dit is de admin-variant die het gesprek
  // bij beide deelnemers definitief weghaalt.
  async function deleteConversation(conv: DmConversation, naam: string): Promise<void> {
    if (
      !window.confirm(
        `Gesprek met ${naam} verwijderen? Alle berichten gaan definitief verloren, ` +
          `ook bij de andere deelnemer. Dit kan niet ongedaan worden gemaakt.`,
      )
    ) {
      return;
    }
    try {
      await apiDelete(`/api/v1/dm/conversations/${conv.id}`);
      select(null);
      loadConversations();
    } catch {
      window.alert('Verwijderen is niet gelukt.');
    }
  }

  async function deleteChannel(thread: ThemaThread): Promise<void> {
    if (
      !window.confirm(
        `Kanaal "${thread.name}" verwijderen? Alle berichten in dit kanaal ` +
          `gaan definitief verloren. Dit kan niet ongedaan worden gemaakt.`,
      )
    ) {
      return;
    }
    await apiDelete(`/api/v1/thema-threads/${thread.id}`);
    if (active?.kind === 'channel' && active.id === thread.id) select(null);
    loadThreads();
  }

  async function createChannel(): Promise<void> {
    const name = (newChannelName ?? '').trim();
    if (!name) return;
    const d = await apiPost<{ thread: ThemaThread }>('/api/v1/thema-threads', { name });
    setNewChannelName(null);
    loadThreads();
    select({ kind: 'channel', id: d.thread.id });
  }

  function memberById(id: string): SmoelenboekEntry | undefined {
    return members.find((m) => m.id === id);
  }

  const searchResults = useMemo(() => {
    if (search === null) return [];
    const q = search.trim().toLowerCase();
    const pool = members.filter((m) => m.id !== myId);
    if (!q) return pool.slice(0, 30);
    return pool
      .filter(
        (m) =>
          m.displayName.toLowerCase().includes(q) ||
          (m.regio ?? '').toLowerCase().includes(q) ||
          (m.specialisaties ?? []).some((s) => s.toLowerCase().includes(q)),
      )
      .slice(0, 30);
  }, [search, members, myId]);

  const activeConv =
    active?.kind === 'dm' ? conversations.find((c) => c.id === active.id) : undefined;
  const activeThread =
    active?.kind === 'channel' ? threads.find((t) => t.id === active.id) : undefined;
  const otherMember = activeConv ? memberById(otherParticipantId(activeConv, myId)) : undefined;
  const activeSelf = activeConv ? isSelfConv(activeConv) : false;
  // Gestart-maar-leeg verbergen; leeg-door-verwijderen (lastMessageAt bewoog) blijft staan.
  const zichtbareConvs = conversations.filter(
    (c) => c.lastMessagePreview || c.lastMessageAt !== c.createdAt || active?.id === c.id,
  );
  const onlineIds = useOnlineUserIds();
  const otherOnline = !!otherMember && !activeSelf && onlineIds.has(otherMember.id);
  // Laatste eigen, bevestigde bericht dat de ander na verzending nog opende.
  const otherReadAt =
    activeConv && !activeSelf
      ? activeConv.lastReadAt[otherParticipantId(activeConv, myId)]
      : undefined;
  const laatsteEigen = [...messages].reverse().find((m) => m.senderId === myId && !m.status);
  const gelezenId =
    otherReadAt && laatsteEigen && laatsteEigen.createdAt <= otherReadAt
      ? laatsteEigen.id
      : undefined;

  return (
    <Layout>
      <div className="flex h-full">
        {/* Sidebar: channels + DMs — on mobile the list and thread swap as full-width panels */}
        <aside
          className={`${active ? 'hidden md:flex' : 'flex'} w-full shrink-0 flex-col border-r border-brand-100 bg-white md:w-72`}
        >
          {/* Geen eigen kop: de paginatitel in de header heet al Chats. */}
          <div className="flex items-center gap-2 border-b border-brand-100 px-3 py-3">
            <div className="flex flex-1 items-center gap-2 rounded-lg border border-brand-100 bg-brand-50/40 px-2.5 py-1.5 focus-within:border-brand-400 focus-within:bg-white">
              <SearchIcon size={14} className="shrink-0 text-ink-900/40" />
              <input
                value={search ?? ''}
                onFocus={() => setSearch((s) => s ?? '')}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch(null);
                }}
                placeholder="Zoek een collega…"
                aria-label="Zoek een collega om een gesprek te starten"
                className="w-full bg-transparent text-[16px] text-ink-900 placeholder:text-ink-900/40 focus:outline-none md:text-[13px]"
              />
              {search !== null && (
                <button
                  type="button"
                  onClick={() => setSearch(null)}
                  aria-label="Zoeken sluiten"
                  className="shrink-0 text-ink-900/40 hover:text-ink-900"
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
            {!demo && (
              <button
                type="button"
                onClick={() => setSearch(search === null ? '' : null)}
                title="Nieuw gesprek"
                aria-label="Nieuw gesprek"
                className="flex h-8 shrink-0 items-center gap-1 rounded-lg bg-brand-600 px-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-brand-700"
              >
                <PlusIcon size={14} />
                Nieuw
              </button>
            )}
          </div>

          <div className="thin-scroll flex-1 overflow-y-auto">
            {/* Nieuw gesprek: collega kiezen. In demo blijft alleen het
                berichtenzoeken over, dus dan pas tonen als er treffers zijn:
                anders staat er een leeg kader onder het zoekveld. */}
            {search !== null && (!demo || (treffers !== null && treffers !== 'fout')) && (
              <div className="border-b border-brand-100 bg-brand-50/40 p-3">
                {/* Collega kiezen start een DM (POST): in de demo alleen de
                    berichtentreffers hieronder, die zijn read-only. */}
                <div className={`max-h-64 overflow-y-auto ${demo ? 'hidden' : ''}`}>
                  {searchResults.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => void startDm(m.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-white"
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100">
                        <span className="text-[11px] font-bold text-brand-700">
                          {initials(m.displayName)}
                        </span>
                      </div>
                      <span className="truncate text-[13px] text-ink-900">{m.displayName}</span>
                    </button>
                  ))}
                  {searchResults.length === 0 && (
                    <p className="px-2 py-2 text-[12px] text-ink-900/45">
                      Geen medewerkers gevonden.
                    </p>
                  )}
                </div>
                {treffers !== null && treffers !== 'fout' && (
                  <div className="mt-2 border-t border-brand-100 pt-2">
                    <p className="px-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-ink-900/40">
                      Berichten
                    </p>
                    <div className="max-h-72 overflow-y-auto">
                      {treffers.map((h) => {
                        const titel =
                          h.kind === 'channel'
                            ? `# ${h.title ?? ''}`
                            : (memberById(
                                h.participants?.find((p) => p !== myId) ??
                                  h.participants?.[0] ??
                                  '',
                              )?.displayName ?? ZELFGESPREK);
                        return (
                          <button
                            key={h.message.id}
                            type="button"
                            onClick={() => {
                              setMarkeerId(h.message.id);
                              select({ kind: h.kind, id: h.id });
                            }}
                            className="flex w-full flex-col rounded-lg px-2 py-1.5 text-left hover:bg-white"
                          >
                            <span className="flex w-full items-baseline gap-2">
                              <span className="truncate text-[12px] font-semibold text-ink-900">
                                {titel}
                              </span>
                              <span className="ml-auto shrink-0 text-[11px] text-ink-900/40">
                                {listTime(h.message.createdAt)}
                              </span>
                            </span>
                            <span className="line-clamp-2 text-[12px] text-ink-900/60">
                              {h.message.text}
                            </span>
                          </button>
                        );
                      })}
                      {treffers.length === 0 && (
                        <p className="px-2 py-2 text-[12px] text-ink-900/45">
                          Geen berichten gevonden.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {treffers === 'fout' && (
                  <div className="mt-2 border-t border-brand-100 pt-2">
                    <div>
                      <p className="px-2 py-2 text-[12px] text-red-700">
                        Zoeken is niet gelukt. Probeer het zo opnieuw.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Kanalen */}
            <div className="px-4 pb-1 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide text-ink-900/40">
                  Kanalen
                </span>
                {isKanaalBeheerder && (
                  <button
                    type="button"
                    onClick={() => setNewChannelName(newChannelName === null ? '' : null)}
                    title="Nieuw kanaal"
                    className="text-ink-900/40 transition-colors hover:text-brand-600"
                  >
                    <PlusIcon size={15} />
                  </button>
                )}
              </div>
            </div>
            {isKanaalBeheerder && newChannelName !== null && (
              <div className="px-4 pb-2">
                <input
                  autoFocus
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void createChannel();
                    if (e.key === 'Escape') setNewChannelName(null);
                  }}
                  placeholder="Kanaalnaam… (Enter)"
                  className="w-full rounded-lg border border-brand-100 bg-white px-2.5 py-1.5 text-[13px] text-ink-900 placeholder:text-ink-900/40 focus:border-brand-400 focus:outline-none"
                />
              </div>
            )}
            {threads.map((t) => {
              const last = t.lastMessageAt ?? t.createdAt;
              // Nooit geopend: geen getal maar wel vet + stip (-1 = stip).
              const unread =
                active?.id === t.id ? 0 : (kanaalUnread[t.id] ?? (reads[t.id] ? 0 : last ? -1 : 0));
              return (
                <ChannelItem
                  key={t.id}
                  thread={t}
                  unread={unread}
                  isActive={active?.kind === 'channel' && active.id === t.id}
                  onClick={() => select({ kind: 'channel', id: t.id })}
                />
              );
            })}
            {threads.length === 0 && (
              <p className="px-4 py-2 text-[12px] text-ink-900/45">Nog geen kanalen.</p>
            )}

            {/* Directe berichten */}
            <div className="px-4 pb-1 pt-4">
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink-900/40">
                Directe berichten
              </span>
            </div>
            <div className="divide-y divide-brand-50">
              {/* Lege gesprekken (gestart, nooit iets gestuurd) alleen tonen zolang ze open staan. */}
              {zichtbareConvs.map((conv) => (
                <DmItem
                  key={conv.id}
                  conv={conv}
                  otherUser={memberById(otherParticipantId(conv, myId))}
                  myId={myId}
                  isActive={active?.kind === 'dm' && active.id === conv.id}
                  onClick={() => select({ kind: 'dm', id: conv.id })}
                />
              ))}
              {zichtbareConvs.length === 0 && (
                <p className="px-4 py-2 text-[12px] text-ink-900/45">
                  Nog geen gesprekken. Start er een via + of het Smoelenboek.
                </p>
              )}
            </div>
          </div>
        </aside>

        {/* Thread pane */}
        <div className={`${active ? 'flex' : 'hidden md:flex'} relative min-w-0 flex-1 flex-col`}>
          {!active && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <MessageCircleIcon size={40} className="text-ink-900/15" />
              <p className="text-[14px] font-medium text-ink-900/40">
                Kies een kanaal of gesprek, of start een nieuw bericht met +.
              </p>
            </div>
          )}

          {active && (
            <>
              <div className="flex items-start gap-3 border-b border-brand-100 bg-white px-4 py-3.5 md:px-5">
                <button
                  type="button"
                  onClick={() => select(null)}
                  aria-label="Terug naar overzicht"
                  className="-ml-1 mt-0.5 shrink-0 rounded-md p-1 text-ink-900/60 hover:bg-brand-50 md:hidden"
                >
                  <ArrowLeftIcon size={18} />
                </button>
                {active.kind === 'channel' ? (
                  <>
                    {bewerkt && activeThread ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          void saveChannel(activeThread);
                        }}
                        className="min-w-0 flex-1 space-y-1.5"
                      >
                        <input
                          value={bewerkNaam}
                          onChange={(e) => setBewerkNaam(e.target.value)}
                          maxLength={100}
                          autoFocus
                          aria-label="Kanaalnaam"
                          className="w-full rounded-md border border-brand-200 px-2 py-1 text-[14px] font-semibold text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                        <input
                          value={bewerkOnderschrift}
                          onChange={(e) => setBewerkOnderschrift(e.target.value)}
                          maxLength={280}
                          placeholder="Bijv. waarvoor dit kanaal bedoeld is, of een gedragsregel"
                          aria-label="Onderschrift"
                          className="w-full rounded-md border border-brand-200 px-2 py-1 text-[12px] text-ink-900/70 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            className="rounded-md bg-brand-500 px-3 py-1 text-[12px] font-semibold text-white hover:bg-brand-600"
                          >
                            Opslaan
                          </button>
                          <button
                            type="button"
                            onClick={() => setBewerkt(false)}
                            className="rounded-md px-2 py-1 text-[12px] font-medium text-ink-900/60 hover:bg-brand-50"
                          >
                            Annuleren
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-[14px] font-semibold text-ink-900">
                          <HashIcon size={15} className="text-brand-600" />
                          {activeThread?.name ?? active.id}
                        </p>
                        {activeThread?.description?.trim() && (
                          <p className="mt-0.5 text-[12px] text-ink-900/50">
                            {activeThread.description}
                          </p>
                        )}
                      </div>
                    )}
                    {isKanaalBeheerder && activeThread && !bewerkt && (
                      <button
                        type="button"
                        onClick={() => {
                          setBewerkNaam(activeThread.name);
                          setBewerkOnderschrift(activeThread.description ?? '');
                          setBewerkt(true);
                        }}
                        title="Kanaal bewerken"
                        aria-label={`Kanaal ${activeThread.name} bewerken`}
                        className="shrink-0 rounded-md border border-brand-200 p-1.5 text-brand-700 transition-colors hover:bg-brand-50"
                      >
                        <PencilIcon size={15} />
                      </button>
                    )}
                    {isKanaalBeheerder && activeThread && !bewerkt && (
                      <button
                        type="button"
                        onClick={() => void deleteChannel(activeThread)}
                        title="Kanaal verwijderen"
                        aria-label={`Kanaal ${activeThread.name} verwijderen`}
                        className="shrink-0 rounded-md border border-red-200 p-1.5 text-red-700 transition-colors hover:bg-red-50"
                      >
                        <Trash2Icon size={15} />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-semibold text-ink-900">
                      {activeSelf ? ZELFGESPREK : (otherMember?.displayName ?? active.id)}
                    </p>
                    {!activeSelf && otherMember?.regio && (
                      <p className="text-[12px] text-ink-900/50">{otherMember.regio}</p>
                    )}
                  </div>
                )}
                {activeConv && !activeSelf && !demo && (
                  <button
                    type="button"
                    onClick={() => void hideConversation(activeConv)}
                    title="Gesprek verbergen (komt terug bij een nieuw bericht)"
                    aria-label="Gesprek verbergen"
                    className="shrink-0 rounded-md border border-brand-200 p-1.5 text-ink-900/60 transition-colors hover:bg-brand-50"
                  >
                    <EyeOffIcon size={15} />
                  </button>
                )}
                {isAdmin && activeConv && !activeSelf && !demo && (
                  <button
                    type="button"
                    onClick={() =>
                      void deleteConversation(activeConv, otherMember?.displayName ?? active.id)
                    }
                    title="Gesprek verwijderen (voor beide deelnemers)"
                    aria-label="Gesprek verwijderen"
                    className="shrink-0 rounded-md border border-red-200 p-1.5 text-red-700 transition-colors hover:bg-red-50"
                  >
                    <Trash2Icon size={15} />
                  </button>
                )}
              </div>

              <div
                onScroll={(e) => {
                  const el = e.currentTarget;
                  onderaanRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                  if (onderaanRef.current && nieuwOnder) setNieuwOnder(false);
                }}
                className="thin-scroll relative flex flex-1 flex-col overflow-y-auto overscroll-contain bg-[#f7fafa] px-4 py-4 md:px-5"
              >
                {/* Kaart bovenin; de berichten duwen vanaf de onderkant omhoog,
                    zodat een kort gesprek niet boven een zee van wit hangt. */}
                {active.kind === 'dm' && (
                  <ProfielKaart member={otherMember} self={activeSelf} online={otherOnline} />
                )}
                <div className="mt-auto space-y-3">
                  {messages.map((m, i) => {
                    const vorige = messages[i - 1];
                    const volgende = messages[i + 1];
                    const nieuweDag =
                      !vorige || dayLabel(vorige.createdAt) !== dayLabel(m.createdAt);
                    const groep = (a: Msg | undefined, b: Msg): boolean =>
                      !!a &&
                      a.senderId === b.senderId &&
                      Math.abs(+new Date(b.createdAt) - +new Date(a.createdAt)) < 5 * 60_000;
                    return (
                      <div
                        key={m.id}
                        id={`msg-${m.id}`}
                        className={`space-y-3 rounded-2xl transition-shadow ${markeerId === m.id ? 'ring-2 ring-mosterdgeel-400/70' : ''}`}
                      >
                        {nieuweDag && (
                          <p className="text-center text-[11px] font-medium text-ink-900/40">
                            {dayLabel(m.createdAt)}
                          </p>
                        )}
                        <MessageBubble
                          message={m}
                          isMine={m.senderId === myId}
                          gelezen={m.id === gelezenId}
                          vervolg={!nieuweDag && groep(vorige, m)}
                          vervolgd={
                            !!volgende &&
                            dayLabel(volgende.createdAt) === dayLabel(m.createdAt) &&
                            groep(m, volgende)
                          }
                          onRetry={(msg) => void verstuur(active, msg)}
                          onDelete={
                            !demo &&
                            (m.senderId === myId ||
                              (isKanaalBeheerder && active.kind === 'channel'))
                              ? (msg) => void deleteMessage(active, msg)
                              : undefined
                          }
                          senderName={
                            active.kind === 'channel'
                              ? memberById(m.senderId)?.displayName
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                <div ref={messagesEndRef} />
              </div>
              {nieuwOnder && (
                <button
                  type="button"
                  onClick={() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    setNieuwOnder(false);
                  }}
                  className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-md hover:bg-brand-700"
                >
                  Nieuwe berichten
                </button>
              )}

              {demo ? (
                <div className="border-t border-brand-100 bg-white px-4 py-3 text-center text-[12px] text-ink-900/55">
                  In deze demo lees je bestaande gesprekken. Berichten versturen kan in de echte
                  omgeving.
                </div>
              ) : (
                <div className="border-t border-brand-100 bg-white px-4 py-3">
                  {(klaarBijlagen.length > 0 || uploadBezig || bijlageFout) && (
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                      {klaarBijlagen.map((b) => (
                        <span
                          key={b.object}
                          className="flex max-w-[220px] items-center gap-1 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-[12px] text-ink-900 ring-1 ring-brand-100"
                        >
                          <FileTextIcon size={13} className="shrink-0 text-brand-700" />
                          <span className="truncate">{b.filename}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setKlaarBijlagen((all) => all.filter((x) => x.object !== b.object))
                            }
                            aria-label={`${b.filename} weghalen`}
                            className="shrink-0 rounded-full p-0.5 text-ink-900/40 hover:bg-brand-100 hover:text-ink-900"
                          >
                            <XIcon size={12} />
                          </button>
                        </span>
                      ))}
                      {uploadBezig && (
                        <span className="text-[12px] text-ink-900/55">Bijlage uploaden…</span>
                      )}
                      {bijlageFout && (
                        <span role="alert" className="text-[12px] text-red-700">
                          {bijlageFout}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="relative">
                    {active.kind === 'dm' && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={Object.keys(BON_EXT).join(',')}
                          className="hidden"
                          onChange={(e) => void voegBijlagenToe([...(e.target.files ?? [])])}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadBezig || sending}
                          aria-label="Bijlage toevoegen"
                          title="Bijlage toevoegen (PDF, afbeelding, Word, Excel)"
                          className="absolute bottom-1.5 left-1.5 flex h-8 w-8 items-center justify-center rounded-full text-ink-900/50 transition-colors hover:bg-brand-100 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-40"
                        >
                          <PaperclipIcon size={16} />
                        </button>
                      </>
                    )}
                    <textarea
                      ref={textareaRef}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void handleSend();
                        }
                      }}
                      placeholder="Typ een bericht… (Enter om te versturen)"
                      rows={1}
                      maxLength={4000}
                      className={`thin-scroll block w-full resize-none rounded-xl border border-brand-100 bg-brand-50/50 py-2.5 ${active.kind === 'dm' ? 'pl-11' : 'pl-4'} pr-12 text-[16px] leading-[22px] text-ink-900 placeholder:text-ink-900/40 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 md:text-[13px]`}
                    />
                    <button
                      type="button"
                      onClick={() => void handleSend()}
                      disabled={(!draft.trim() && !klaarBijlagen.length) || sending || uploadBezig}
                      aria-label="Verstuur bericht"
                      title="Verstuur (Enter)"
                      className="absolute bottom-1.5 right-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm transition-all duration-150 hover:bg-brand-700 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:bg-brand-200 disabled:text-white/80 disabled:shadow-none"
                    >
                      <SendHorizontalIcon size={15} className={sending ? 'animate-pulse' : ''} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
